// @flow

import _ from 'lodash';
import { $none, $set, update } from 'qim';

import { singular } from '@zapier/common-utils';

import * as DeveloperActions from 'app/developer-v3/Actions';
import { getCurrentPathname } from 'app/common/utils/RouterSelectors';
import { hasUnsavedChanges } from 'app/developer-v3/utils/selectors';
import { isSubUrlOf } from 'app/common/utils/UrlUtils';
import { notify } from 'app/developer-v3/store/shared/actions';
import { quickCopy } from 'app/developer-v3/utils/mergeDefinitions';
import {
  ACTION_INPUT_TYPES,
  ACTION_TYPES,
  APP_INTENTION,
  APP_STATUSES,
  APP_STATUS_INVITE_ONLY,
  APP_STATUS_PENDING,
  APP_STATUS_PRIVATE,
  APP_STATUS_PUBLIC,
  APP_STATUS_UNAVALIABLE,
  BASE_APP_URL,
  BASE_APP_URL_LEGACY,
  KEY_SUFFIX,
  MUTATION_TYPES,
  NEW_SERVICE_FIELD,
  SERVICE_TYPES,
  WRITE_METHODS,
} from 'app/developer-v3/utils/constants';

import type {
  ActionInputType,
  ActionType,
  AppFormType,
  AppService,
  AppType,
  AppTypeAll,
  BackendAppType,
  ObjectType,
  OperationMethodType,
  PartialDefinition,
  ServiceType,
} from 'app/developer-v3/types';
import type {
  AuthenticationSchema,
  AuthenticationType,
} from 'app/developer-v3/platformSchema/authentication';
import type { Dispatch, GetState, StringMap } from 'app/common/types';
import type {
  FieldOrFunctionSchema,
  FieldSchema,
} from 'app/developer-v3/platformSchema/field';
import type { KeyValue } from 'app/developer-v3/components/KeyValueEditor';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';

type BundleType = 'inputData' | 'authData';

export const titleize = _.flow(
  singular,
  _.capitalize
);

export const isActionType = (serviceType: ServiceType) =>
  Object.keys(ACTION_TYPES).includes(serviceType);

export const createKeyFromService = (service: ServiceType) =>
  isActionType(service) ? 'actionKey' : `${singular(service)}Key`;

export const prettyFormatJsonString = (obj: {} | Object[]): string =>
  JSON.stringify(obj, null, 2);

export const maybePrettyJsonString = (string: string): string => {
  try {
    const object = JSON.parse(string);
    return prettyFormatJsonString(object);
  } catch (e) {
    return string;
  }
};

export const sortAppsByStatus = (
  apps: Array<AppType>,
  isDesc?: boolean = true
) => {
  const order = isDesc ? [...APP_STATUSES].reverse() : APP_STATUSES;
  return _.sortBy(apps, app => order.indexOf(app.status));
};

export const parseBackendAppStatus = ({
  app,
  isLegacy,
}: {
  app: BackendAppType,
  isLegacy: boolean,
}) =>
  isLegacy
    ? APP_STATUSES[Number(app.status)] || APP_STATUS_UNAVALIABLE
    : app.pending
    ? APP_STATUS_PENDING
    : app.public
    ? APP_STATUS_PUBLIC
    : APP_STATUS_PRIVATE;

export const parseBackendAppToAppType = ({
  isLegacy,
}: {
  isLegacy: boolean,
}) => (app: BackendAppType): AppTypeAll =>
  isLegacy
    ? {
        id: app.id,
        image: app.image,
        isBeta:
          parseBackendAppStatus({ app, isLegacy: true }) ===
            APP_STATUS_INVITE_ONLY ||
          app.is_beta ||
          false,
        isPending:
          parseBackendAppStatus({ app, isLegacy: true }) === APP_STATUS_PENDING,
        isPublic:
          parseBackendAppStatus({ app, isLegacy: true }) === APP_STATUS_PUBLIC,
        status: parseBackendAppStatus({ app, isLegacy: true }),
        title: app.title,
      }
    : {
        id: app.id,
        title: app.title,
        isBeta: app.is_beta || false,
        isEarlyAccess: app.early_access,
        isPublic: app.public,
        isPending: app.pending,
        status: parseBackendAppStatus({ app, isLegacy: false }),
        version: app.latest_version,
        versions: app.versions,
        selectedApi: `${app.key}${KEY_SUFFIX}`,
      };

export const getValidAppIntention = (intention: string): string =>
  _.includes(APP_INTENTION, intention) ? intention : APP_INTENTION.emptyString;

export const parseBackendAppToAppFormType = (
  app: BackendAppType
): AppFormType => ({
  title: app.title,
  description: app.description,
  homepage_url: app.homepage_url,
  intention: getValidAppIntention(app.intention),
  app_category: app.app_category,
  app_category_other: app.app_category_other,
  image: app.image,
  role: app.role,
  subscription: app.subscription,
});

export const getAppVersionUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${BASE_APP_URL}/app/${appId}/version/${appVersionNumber}`;

export const getAppUrl = (
  appId: number | string,
  isLegacy: boolean = false
): string => `${isLegacy ? BASE_APP_URL_LEGACY : BASE_APP_URL}/app/${appId}`;

export const convertParamsToItems = (params: StringMap): Array<KeyValue> =>
  Object.keys(params).map(key => ({ key, value: params[key] }));

export const convertItemsToParams = (items: Array<KeyValue>): StringMap =>
  items.reduce((params, item) => ({ ...params, [item.key]: item.value }), {});

export const getServiceUrl = (
  type: ServiceType,
  serviceOptions: {
    id: string,
    version: string,
    [serviceKey: string]: string,
    actionType?: ActionType,
  }
) => {
  const { id: appId, version, actionType, actionKey } = serviceOptions;
  const serviceKey = serviceOptions[createKeyFromService(type)];
  const baseUrl = `${getAppVersionUrl(appId, version)}`;

  return actionType
    ? `${baseUrl}/actions/${actionType}/${actionKey}`
    : `${baseUrl}/${type}/${serviceKey}`;
};

export const getTriggerUrl = _.partial(getServiceUrl, 'triggers');
export const getActionUrl = _.partial(getServiceUrl, 'actions');

export const isMenuClick = (event: SyntheticEvent<*>) => {
  const columns = event.currentTarget.children;
  const menuColumn = columns[columns.length - 1];

  return menuColumn.contains(event.target);
};

export const getBaseAuthUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${getAppVersionUrl(appId, appVersionNumber)}/authentication`;

export const getAuthUrlForFieldKey = (
  appId: number | string,
  appVersionNumber: string,
  fieldKey: string
): string => `${getBaseAuthUrl(appId, appVersionNumber)}/fields/${fieldKey}`;

export const getAdvancedUrl = (
  appId: number | string,
  appVersionNumber: string
) => `${getAppVersionUrl(appId, appVersionNumber)}/advanced`;

export function reorderList<T>(
  list: Array<T>,
  targetIndex: number,
  newIndex: number
): Array<T> {
  const targetItem = list[targetIndex];
  const listCopy = list.slice(0);
  const adjustedNewIndex = newIndex === -1 ? list.length : newIndex;

  listCopy.splice(targetIndex, 1);
  listCopy.splice(adjustedNewIndex, 0, targetItem);

  return listCopy;
}

export const makeTextHtmlSafe = (value: string): string =>
  _.escape(String(value));

export const convertPlainTextToHtml = (value: string): string =>
  makeTextHtmlSafe(value)
    .split('\n')
    .join('<br />');

const versionIncrementors = [
  // patch
  (versionParts: Array<number>) => {
    return [versionParts[0], versionParts[1], versionParts[2] + 1].join('.');
  },
  // minor
  (versionParts: Array<number>) => {
    return [versionParts[0], versionParts[1] + 1, 0].join('.');
  },
  // major
  (versionParts: Array<number>) => {
    return [versionParts[0] + 1, 0, 0].join('.');
  },
];
const versionLabels = ['Patch', 'Minor', 'Major'];

export const incrementVersion = (
  version: string // must be in the form a.b.c
): Array<{ value: string, label: string }> => {
  const versionParts = version.split('.').map(i => parseInt(i, 10));

  return versionIncrementors.map((incrementor, index) => {
    const newVersion = incrementor(versionParts);
    return {
      value: newVersion,
      label: `${newVersion} (${versionLabels[index]})`,
    };
  });
};

export const buildPartialDefinition = (
  objectType: ObjectType,
  objectKey: string,
  operationMethod: OperationMethodType,
  request: RequestSchema
): PartialDefinition => {
  if (objectType === 'authentication') {
    return {
      [objectType]: {
        [operationMethod]: request,
      },
    };
  }

  const definitionKey =
    objectType === 'search' ? `${objectType}es` : `${objectType}s`;

  return {
    [definitionKey]: {
      [objectKey]: {
        operation: {
          [operationMethod]: request,
        },
      },
    },
  };
};

export const warnIfLeavingUnsavedChanges = ({
  cancelRoute,
  getState,
  dispatch,
}: {
  cancelRoute: () => void,
  getState: GetState,
  dispatch: Dispatch,
}) => {
  const state = getState();
  if (hasUnsavedChanges(state)) {
    cancelRoute();
    const message = `It seems you have made changes that weren't saved yet. Please save them, or try going again where you were going.`;
    dispatch(notify.info(message));
    dispatch(DeveloperActions.setUnsavedChanges(false));
  }
};

export const getInputFieldType = (
  field: FieldOrFunctionSchema
): ActionInputType => {
  if (field.source) {
    return ACTION_INPUT_TYPES.DYNAMIC;
  }

  if (field.children) {
    return ACTION_INPUT_TYPES.GROUP;
  }

  return ACTION_INPUT_TYPES.INPUT;
};

export const removeNonNumericAndCastToNumber = (str: string) => {
  return Number(str.replace(/\D/g, ''));
};

export const getSettingsSaveText = (key: string) =>
  key === NEW_SERVICE_FIELD ? 'Save and Continue' : 'Save';

const makeBundlize = (bundleType: BundleType) => (key: string) =>
  `{{bundle.${bundleType}.${key}}}`;
export const bundlizeAuthData = makeBundlize('authData');
export const bundlizeInputData = makeBundlize('inputData');

const getRequestMethod = (request, serviceType) => {
  const defaultMethod = serviceType === SERVICE_TYPES.creates ? 'POST' : 'GET';
  return request.method || defaultMethod;
};

// converts "api_key" to "X-API-KEY", and "api key" to "X-API-KEY"
export const getHeaderKey = (fieldKey: string): string =>
  `X-${_.toUpper(_.kebabCase(fieldKey))}`;

const addFieldToRequest = (
  fields,
  newFields,
  request,
  bundlize,
  serviceType
) => {
  const [newField] = _.differenceBy(newFields, fields, 'key');
  const requestMethod = getRequestMethod(request, serviceType);
  const requestKey = WRITE_METHODS.includes(requestMethod) ? 'body' : 'params';

  return update(
    [requestKey, newField.key, $set(bundlize(newField.key))],
    request
  );
};

const removeFieldFromRequest = (fields, newFields, request) => {
  const [field] = _.differenceBy(fields, newFields, 'key');

  const newRequest = update(
    [['body', field.key, $none], ['params', field.key, $none]],
    request
  );
  if (_.isEmpty(newRequest.body)) {
    delete newRequest.body;
  }
  if (_.isEmpty(newRequest.params)) {
    delete newRequest.params;
  }

  return newRequest;
};

const updateFieldInRequest = (fields, newFields, request, bundlize) => {
  const [field] = _.differenceBy(fields, newFields, 'key');
  const newRequest = quickCopy(request);

  if (!field) {
    return newRequest;
  }

  const [newField] = _.differenceBy(newFields, fields, 'key');
  const fieldIndex = _.findIndex(fields, ['key', field.key]);
  const newFieldIndex = _.findIndex(newFields, ['key', newField.key]);
  const { key } = fields[fieldIndex];
  const { key: newKey } = newFields[newFieldIndex];
  const bundleField = bundlize(key);
  const newBundleField = bundlize(newKey);

  if (newRequest.headers) {
    delete newRequest.headers[getHeaderKey(key)];
    newRequest.headers[getHeaderKey(newKey)] = newBundleField;
  }

  if (newRequest.body) {
    delete newRequest.body[key];
    newRequest.body[newKey] = newBundleField;
  }

  if (newRequest.params) {
    delete newRequest.params[key];
    newRequest.params[newKey] = newBundleField;
  }

  if (newRequest.url) {
    newRequest.url = newRequest.url.replace(bundleField, newBundleField);
  }

  return newRequest;
};

const getFieldMutationType = (fields, newFields) => {
  if (fields.length > newFields.length) {
    return MUTATION_TYPES.remove;
  }

  if (fields.length < newFields.length) {
    return MUTATION_TYPES.add;
  }

  return MUTATION_TYPES.update;
};

const mutateFieldsInRequest = (
  bundlize: string => string,
  fields: FieldSchema[] = [],
  newFields: FieldSchema[] = [],
  request: RequestSchema,
  serviceType?: ServiceType
): RequestSchema => {
  const newRequest = quickCopy(request);

  if (request.source) {
    return newRequest;
  }

  const mutationType = getFieldMutationType(fields, newFields);
  return mutationType === MUTATION_TYPES.add
    ? addFieldToRequest(fields, newFields, newRequest, bundlize, serviceType)
    : mutationType === MUTATION_TYPES.update
    ? updateFieldInRequest(fields, newFields, newRequest, bundlize)
    : removeFieldFromRequest(fields, newFields, newRequest);
};

const mutateAuthFieldsInRequest = _.partial(
  mutateFieldsInRequest,
  bundlizeAuthData
);
const mutateInputFieldsInRequest = _.partial(
  mutateFieldsInRequest,
  bundlizeInputData
);

// Mutate headers/body/params.
export const mutateAuthFieldsInTestRequest = (
  authentication: AuthenticationSchema,
  fields: Array<FieldSchema> = [],
  newFields: Array<FieldSchema> = []
): RequestSchema => {
  const authType = authentication.type;

  const newAuthentication = update(
    [
      ['fields', $set(newFields)],
      ['test', $set(_.get(authentication, ['test'], {}))],
    ],
    authentication
  );

  if (
    _.get(authentication, ['test', 'source']) ||
    _.get(authentication, ['test', 'require'])
  ) {
    return newAuthentication;
  }

  const newRequest = mutateAuthFieldsInRequest(
    fields,
    newFields,
    newAuthentication.test
  );

  if (authType === 'oauth2') {
    newRequest.headers = {
      ...newRequest.headers,
      Authorization: `Bearer {{bundle.authData.access_token}}`,
    };
  }

  newRequest.headers = _.omit(
    newRequest.headers,
    _.map(fields, ({ key }) => getHeaderKey(key))
  );
  newFields.forEach(({ key }) => {
    newRequest.headers = {
      ...newRequest.headers,
      [getHeaderKey(key)]: bundlizeAuthData(key),
    };
  });
  if (_.isEmpty(newRequest.headers)) {
    delete newRequest.headers;
  }

  if (authType !== 'oauth2') {
    newRequest.params = _.omit(newRequest.params, _.map(fields, 'key'));
    newFields.forEach(({ key }) => {
      newRequest.params = {
        ...newRequest.params,
        [key]: bundlizeAuthData(key),
      };
    });
    if (_.isEmpty(newRequest.params)) {
      delete newRequest.params;
    }
  }

  return update(['test', $set(newRequest)], newAuthentication);
};

// Include in request's headers/params.
export const includeAuthFieldsInRequest = (
  authType: AuthenticationType,
  fields: FieldSchema[],
  request: RequestSchema
): RequestSchema => {
  if (request.source || request.require) {
    return request;
  }

  const newRequest = quickCopy(request);

  if (authType === 'oauth2') {
    newRequest.headers = {
      ...newRequest.headers,
      Authorization: `Bearer {{bundle.authData.access_token}}`,
    };
  }

  if (fields.length === 0) {
    return newRequest;
  }

  fields.forEach(({ key }) => {
    const headerKey = getHeaderKey(key);
    newRequest.headers = {
      ...newRequest.headers,
      [headerKey]: bundlizeAuthData(key),
    };
  });

  if (authType !== 'oauth2') {
    fields.forEach(({ key }) => {
      newRequest.params = {
        ...newRequest.params,
        [key]: bundlizeAuthData(key),
      };
    });
  }

  return newRequest;
};

// Include in body and/or params.
export const includeInputFieldsInOperation = (
  newService: AppService,
  fields: Array<FieldSchema> = [],
  serviceType: ServiceType
): AppService => {
  const newPerform = mutateInputFieldsInRequest(
    fields,
    newService.operation.inputFields,
    newService.operation.perform,
    serviceType
  );
  return update(['operation', 'perform', $set(newPerform)], newService);
};

export const arrayToCsv = (values: string[]): string =>
  Array.isArray(values) ? values.join(', ') : values.toString();

export const csvToArray = (values: string): string | string[] =>
  values.includes(',') ? values.split(',').map(item => item.trim()) : values;

export const isNewServiceField = (
  inputField: number | string | { key: string }
): boolean => {
  if (!inputField) {
    return false;
  }

  if (typeof inputField === 'number') return false;
  if (typeof inputField === 'string') return inputField === NEW_SERVICE_FIELD;
  return inputField.key === NEW_SERVICE_FIELD;
};

export const findByKeyOrIndex = <T>(
  array: T[],
  selector: number | string
): T | void => {
  const found = _.find(array, { key: selector });
  if (found) {
    return found;
  }

  const index = parseInt(selector, 10);
  if (Number.isInteger(index) && index < array.length) {
    return array[index];
  }

  return undefined;
};

export const createChangelogString = ({
  reasons,
  description,
}: {
  reasons: { [key: string]: boolean },
  description: string,
}): string =>
  Object.keys(reasons)
    .filter((key: string) => reasons[key])
    .map(_.startCase)
    .concat(description)
    .join(', ');

export const isLegacyIntegration = (integration: AppTypeAll) =>
  !integration.hasOwnProperty('version');

export const isVBOrLegacyUrl = (state: Object) =>
  isSubUrlOf(getCurrentPathname(state), BASE_APP_URL) ||
  isSubUrlOf(getCurrentPathname(state), BASE_APP_URL_LEGACY);
